/**
 * 설치장소 분석 차트 3종 (DESIGN §3.8 · §8.6.2).
 *
 * **색을 하나의 규칙으로 통일했다.** 이 프로젝트의 팔레트에는 카테고리 색이
 * 둘뿐이다(`--c-cat-1` · `--c-cat-2`). 장소군은 9개라 카테고리 색으로는
 * 감당이 안 되고, §8.6.2 는 8색 초과를 금지한다.
 *
 * 그래서 **단일 색상 순차 램프를 규모 순으로** 쓴다. 정체성 인코딩으로는
 * 차선이지만, 세 그래프에서 같은 장소가 같은 색을 갖는 것이 9개 색을 지어내는
 * 것보다 정직하다. 순서가 곧 크기라는 사실도 함께 전달된다.
 * 색만으로 구분하지 않도록 **모든 조각·막대·선에 이름을 직접 라벨**한다.
 */

import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { axisStyle } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'

export interface PlaceRowLite {
  code: string
  n_chargers: number
  n_stations: number
  per_station_p50: number | null
  n_fast: number | null
  n_slow: number | null
  occupancy_min_p50: number | null
  charge_min_p50: number | null
}

export interface DailyPoint {
  date_kst: string
  n_chargers: number
  availability: number | null
  coverage: number | null
  utilization_min_per_charger: number | null
}

interface Props {
  rows: PlaceRowLite[]
  series: Record<string, DailyPoint[]>
  label: (code: string) => string
}

/** 규모 순 단일 색상 램프. 진할수록 크다 */
function ramp(n: number): string[] {
  // Action Blue 를 밝기만 바꿔 쓴다 — 새 색상(hue)을 만들지 않는다
  const stops = ['#0a3f7a', '#0a4f99', '#0060bd', '#2b7fd4', '#5c9fe0', '#8dbdea', '#b7d5f2', '#d6e6f8', '#eaf1fb']
  if (n <= stops.length) return stops.slice(0, n)
  return Array.from({ length: n }, (_, i) => stops[Math.min(i, stops.length - 1)] ?? '#8dbdea')
}

/** 훅이다 — JSX 가 아니라 option 3종을 돌려준다 (컴포넌트 규칙 회피) */
export function usePlaceCharts({ rows, series, label }: Props) {
  const ink3 = cssVar('--c-ink-3', '#626266')
  const hair = cssVar('--c-hairline', '#e0e0e0')
  const na = cssVar('--c-na', '#6f6f75')

  // 규모 순 정렬 — 색·순서·범례가 세 그래프에서 모두 이 순서를 따른다
  const ordered = useMemo(
    () => [...rows].sort((a, b) => b.n_chargers - a.n_chargers),
    [rows],
  )
  const colors = useMemo(() => ramp(ordered.length), [ordered.length])
  const colorOf = useMemo(() => {
    const m = new Map<string, string>()
    ordered.forEach((r, i) => m.set(r.code, r.code === 'unknown' ? na : (colors[i] ?? na)))
    return m
  }, [ordered, colors, na])

  const total = ordered.reduce((a, r) => a + r.n_chargers, 0)

  // ── ① 원형(도넛) — 구성비 ────────────────────────────────────────
  const donut: EChartsOption = useMemo(() => ({
    animation: false,
    tooltip: {
      trigger: 'item',
      formatter: (p: unknown) => {
        const d = p as { name: string; value: number; percent: number }
        return `${d.name}<br/>${int(d.value)}기 · ${d.percent.toFixed(1)}%`
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['46%', '74%'],
        // 도넛으로 두는 이유는 가운데를 총계에 쓰기 위해서다 — 조각 면적
        // 비교의 약점을 숫자로 보완한다
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        label: {
          formatter: '{b}\n{d}%',
          fontSize: 11,
          color: ink3,
          lineHeight: 15,
        },
        labelLine: { length: 8, length2: 10 },
        data: ordered.map((r) => ({
          name: label(r.code),
          value: r.n_chargers,
          itemStyle: { color: colorOf.get(r.code) },
        })),
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: `${int(total)}기`,
          fontSize: 18,
          fontWeight: 600,
          fill: cssVar('--c-ink', '#1d1d1f'),
          textAlign: 'center',
        },
      },
    ],
  }), [ordered, colorOf, label, total, ink3])

  // ── ② 막대 — 급속 비율 (정렬된 가로 막대, §8.6.2) ────────────────
  const bar: EChartsOption = useMemo(() => {
    const data = ordered
      .map((r) => {
        const known = (r.n_fast ?? 0) + (r.n_slow ?? 0)
        return { code: r.code, v: known ? (r.n_fast ?? 0) / known : null, known }
      })
      .filter((d) => d.v != null)
      .sort((a, b) => (a.v ?? 0) - (b.v ?? 0))
    return {
      animation: false,
      grid: { left: 118, right: 56, top: 8, bottom: 28 },
      xAxis: {
        type: 'value', min: 0, max: 1,
        ...axisStyle(ink3, hair),
        axisLabel: { formatter: (v: number) => pct(v, 0), color: ink3, fontSize: 11 },
      },
      yAxis: {
        type: 'category',
        data: data.map((d) => label(d.code)),
        ...axisStyle(ink3, hair),
        axisLabel: { color: ink3, fontSize: 11 },
      },
      tooltip: {
        trigger: 'item',
        formatter: (p: unknown) => {
          const d = p as { name: string; value: number; dataIndex: number }
          const row = data[d.dataIndex]
          return `${d.name}<br/>급속 ${pct(d.value, 1)} (판정 가능 ${int(row?.known ?? 0)}기)`
        },
      },
      series: [
        {
          type: 'bar',
          data: data.map((d) => ({
            value: d.v,
            itemStyle: { color: colorOf.get(d.code), borderRadius: [0, 4, 4, 0] },
          })),
          barMaxWidth: 16,
          label: {
            show: true, position: 'right', fontSize: 11, color: ink3,
            formatter: (p: unknown) => pct((p as { value: number }).value, 1),
          },
        },
      ],
    }
  }, [ordered, colorOf, label, ink3, hair])

  // ── ③ 선형 — 일별 1대당 충전 분 ──────────────────────────────────
  const line: EChartsOption = useMemo(() => {
    const dates = [...new Set(Object.values(series).flat().map((d) => d.date_kst))].sort()
    return {
      animation: false,
      // 오른쪽은 끝 라벨(장소 이름) 자리다 — 좁으면 글자가 잘린다
      grid: { left: 52, right: 132, top: 16, bottom: 30 },
      xAxis: {
        type: 'category', data: dates,
        ...axisStyle(ink3, hair),
        axisLabel: {
          color: ink3, fontSize: 11,
          formatter: (v: string) => v.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        name: '분/기·일',
        nameTextStyle: { color: ink3, fontSize: 11 },
        ...axisStyle(ink3, hair),
        // 세로축도 자연수 — 분 단위에 소수점은 의미가 없다
        axisLabel: {
          color: ink3, fontSize: 11,
          formatter: (v: number) => String(Math.round(v)),
        },
      },
      /**
       * **끝 라벨 대신 세로 범례를 쓴다.**
       * 선 9개의 마지막 값이 좁은 구간에 몰리면 끝 라벨은 원리적으로 겹친다
       * (실측: 마지막 날 6개가 30~110분 안에 들어옴). ECharts 의 endLabel 에는
       * 충돌 회피가 없어 어떻게 배치해도 겹침이 남는다. 범례는 자기 자리를
       * 갖고 순서도 규모 순이라 선을 짚어 가기 쉽다.
       */
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 0,
        top: 8,
        itemWidth: 14,
        itemHeight: 8,
        itemGap: 7,
        textStyle: { fontSize: 10, color: ink3 },
        data: ordered.map((r) => label(r.code)),
      },
      tooltip: {
        trigger: 'axis',
        // 분 단위는 소수점이 의미 없다 — 원본은 15자리까지 온다
        valueFormatter: (v: unknown) =>
          typeof v === 'number' ? `${Math.round(v)}분` : '—',
      },
      series: ordered.map((r, si) => {
        const pts = series[r.code] ?? []
        const byDate = new Map(pts.map((d) => [d.date_kst, d.utilization_min_per_charger]))
        const values = dates.map((d) => byDate.get(d) ?? null)
        /**
         * 끝 라벨이 겹치는 것을 막는다.
         *
         * 9개 선의 마지막 값이 몇 분 안에 몰리면 라벨이 서로 위에 쌓여 읽을 수
         * 없다 (실측: 마지막 날 6개가 30~110분 구간에 겹침). ECharts 의
         * endLabel 은 회피 기능이 없으므로 **직접 자리를 계산한다** —
         * 마지막 값 순위대로 세로 간격을 강제로 벌린다.
         */
        return {
          type: 'line' as const,
          name: label(r.code),
          // 결측일을 0 으로 잇지 않는다 — 선이 끊기는 것이 사실이다
          connectNulls: false,
          data: values,
          showSymbol: false,
          lineStyle: { width: 2, color: colorOf.get(r.code) },
          itemStyle: { color: colorOf.get(r.code) },
          z: 10 - si,
          emphasis: { focus: 'series' as const },
        }
      }),
    }
  }, [ordered, series, colorOf, label, ink3, hair])

  return { donut, bar, line, colorOf, ordered }
}
